import React, { FC, JSX, memo, useState } from 'react';
import { UNAVAILABLE_IMAGE_URL } from '../../constants/general';

interface IProgressiveImageProps {
  src: string;
  alt: string;
  lazy?: boolean;
  width?: string | number;
  height?: string | number;
  className?: string;
  fetchPriority?: 'high' | 'low' | 'auto';
  fallbackSrc?: string; // Optional prop to specify a custom fallback image URL in case of load failure
}

/**
 * A React component that renders an image with a fallback option.
 * Renders the img element immediately so the browser can discover and prioritize
 * it during parsing, avoiding the LCP delay caused by JS-gated image loading.
 * @param {IProgressiveImageProps} props - The props for the ProgressiveImage component.
 * @returns {JSX.Element} The rendered image element.
 */
export const ProgressiveImage: FC<IProgressiveImageProps> = memo(
  ({
    src,
    alt,
    lazy = true,
    width,
    height,
    className,
    fetchPriority,
    fallbackSrc = UNAVAILABLE_IMAGE_URL,
  }): JSX.Element => {
    const [hasError, setHasError] = useState(false);
    const imgSrc = hasError ? fallbackSrc : src;

    return (
      <img
        loading={lazy ? 'lazy' : undefined}
        src={imgSrc}
        alt={alt}
        width={width}
        height={height}
        className={className}
        fetchPriority={fetchPriority}
        onError={() => {
          if (!hasError) {
            setHasError(true);
          }
        }}
        data-testid="progressive-image"
      />
    );
  },
);
